import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Simple file preview controller
// Shows thumbnails/file list for selected files
// Does NOT modify input.files to preserve ActiveStorage direct upload
export default class extends Controller {
  static targets = ["input", "preview"]
  static values = {
    type: String // "photos" or "documents"
  }

  connect() {
    this.uploadingCount = 0
    this.trackDirectUploads()
  }

  handleFileSelect(event) {
    const input = event.target
    const files = Array.from(input.files)

    if (files.length === 0) {
      this.clearPreview()
      return
    }

    this.showPreview(files)
  }

  trackDirectUploads() {
    document.addEventListener('direct-upload:start', (event) => {
      if (this.isOurInput(event.target)) {
        this.uploadingCount++
        this.updateSubmitButton()
      }
    })

    document.addEventListener('direct-upload:end', (event) => {
      if (this.isOurInput(event.target)) {
        this.uploadingCount--
        this.updateSubmitButton()
      }
    })

    document.addEventListener('direct-upload:error', (event) => {
      if (this.isOurInput(event.target)) {
        this.uploadingCount--
        this.updateSubmitButton()
        alert(t('javascript.controllers.file_preview_controller.file_upload_failed_please_try_again'))
      }
    })
  }

  isOurInput(target) {
    return this.hasInputTarget && target === this.inputTarget
  }

  updateSubmitButton() {
    const form = this.element.closest('form')
    if (!form) return

    const button = form.querySelector('[type="submit"]')
    if (!button) return

    // Initialize form-level counter if it doesn't exist
    if (!form.dataset.uploadingCount) {
      form.dataset.uploadingCount = '0'
    }

    // Calculate total uploads across all file-preview controllers
    let totalUploading = parseInt(form.dataset.uploadingCount) || 0

    // Update the form counter with this controller's count
    const oldCount = parseInt(this.element.dataset.uploading || '0')
    totalUploading = totalUploading - oldCount + this.uploadingCount
    form.dataset.uploadingCount = totalUploading.toString()
    this.element.dataset.uploading = this.uploadingCount.toString()

    if (totalUploading > 0) {
      button.disabled = true
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.innerHTML
      }
      button.innerHTML = `
        <span class="material-symbols-outlined animate-spin !text-lg">progress_activity</span>
        ${t("javascript.controllers.file_preview_controller.uploading_files", { count: totalUploading })}
      `
      button.classList.add('opacity-75', 'cursor-not-allowed')
    } else {
      button.disabled = false
      if (button.dataset.originalText) {
        button.innerHTML = button.dataset.originalText
        delete button.dataset.originalText
      }
      button.classList.remove('opacity-75', 'cursor-not-allowed')
    }
  }

  showPreview(files) {
    if (!this.hasPreviewTarget) return

    this.previewTarget.classList.remove('hidden')

    const statusHTML = `
      <div class="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined text-green-400">check_circle</span>
          <div class="text-green-300 text-sm font-medium">
            ${t("javascript.controllers.file_preview_controller.files_selected", { count: files.length })}
          </div>
        </div>
      </div>
    `

    let previewHTML = ''
    if (this.typeValue === 'photos') {
      previewHTML = this.buildPhotoGrid(files)
    } else if (this.typeValue === 'videos') {
      previewHTML = this.buildVideoList(files)
    } else {
      previewHTML = this.buildDocumentList(files)
    }

    this.previewTarget.innerHTML = statusHTML + previewHTML
  }

  clearPreview() {
    if (!this.hasPreviewTarget) return
    this.previewTarget.innerHTML = ''
    this.previewTarget.classList.add('hidden')
  }

  buildPhotoGrid(files) {
    const items = files.map(file => {
      const url = URL.createObjectURL(file)
      return `
        <div class="relative">
          <img src="${url}" 
               class="aspect-square w-full rounded-lg object-cover border border-border-dark"
               alt="${this.escapeHtml(file.name)}">
          <div class="absolute bottom-2 left-2 right-2 bg-black/75 rounded px-2 py-1">
            <div class="text-xs text-white truncate">${this.escapeHtml(file.name)}</div>
            <div class="text-xs text-gray-300">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
      `
    }).join('')

    return `
      <div class="mt-4 pt-4 border-t border-border-dark">
        <h3 class="text-sm font-medium text-[#E0E7FF] mb-3">${t("javascript.controllers.file_preview_controller.photos_count", { count: files.length })}</h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          ${items}
        </div>
      </div>
    `
  }

  buildDocumentList(files) {
    const items = files.map(file => {
      const icon = this.getFileIcon(file)
      return `
        <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-300">
          <div class="flex-shrink-0 text-primary">
            <span class="material-symbols-outlined !text-2xl">${icon}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-gray-900 truncate">${this.escapeHtml(file.name)}</div>
            <div class="text-xs text-gray-500">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
      `
    }).join('')

    return `
      <div class="mt-4 pt-4 border-t border-gray-300">
        <h3 class="text-sm font-medium text-gray-900 mb-3">${t("javascript.controllers.file_preview_controller.documents_count", { count: files.length })}</h3>
        <div class="space-y-2">
          ${items}
        </div>
      </div>
    `
  }

  buildVideoList(files) {
    const items = files.map(file => {
      const url = URL.createObjectURL(file)
      return `
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-300">
          <div class="w-full sm:w-36 aspect-video bg-black rounded overflow-hidden flex-shrink-0">
            <video src="${url}" class="w-full h-full object-contain" preload="metadata" controls></video>
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-gray-900 truncate">${this.escapeHtml(file.name)}</div>
            <div class="text-xs text-gray-500 mt-1">${this.formatFileSize(file.size)} • ${file.type || t('javascript.controllers.file_preview_controller.video')}</div>
          </div>
        </div>
      `
    }).join('')

    return `
      <div class="mt-4 pt-4 border-t border-gray-300">
        <h3 class="text-sm font-medium text-gray-900 mb-3">${t("javascript.controllers.file_preview_controller.videos_count", { count: files.length })}</h3>
        <div class="space-y-3">
          ${items}
        </div>
      </div>
    `
  }

  getFileIcon(file) {
    const type = file.type.toLowerCase()
    const name = file.name.toLowerCase()

    if (type === 'application/pdf') return 'picture_as_pdf'
    if (type.includes('word') || type.includes('document') || name.endsWith('.doc') || name.endsWith('.docx')) return 'description'
    if (type.includes('spreadsheet') || type.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx')) return 'table_chart'
    if (type.includes('zip') || type.includes('compressed')) return 'folder_zip'
    if (type.startsWith('image/')) return 'image'
    return 'insert_drive_file'
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  disconnect() {
    // Clean up object URLs
    if (this.hasPreviewTarget) {
      const images = this.previewTarget.querySelectorAll('img')
      images.forEach(img => {
        if (img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src)
        }
      })
    }
  }
}

